import { useState, useEffect, useMemo } from 'react'
import { fetchTodos, resolveTodo, paradigm } from '../lib/api'
import TodoCard from '../components/TodoCard'
import SkeletonLoader from '../components/SkeletonLoader'

interface Todo {
  id: string
  question: string
  options: string[]
  context?: string
  idea?: string
  blocking?: string[]
  agent_reasoning?: string
  source?: string
  claim_fidelity?: number | null
  created_at?: string
  priority?: string
  todo_type?: string
  // Phase 2 fields (optional — gracefully absent on existing TODOs)
  failure_mode?: string
  expected_result?: string
  suggested_fix?: string
  agent_proposed_fix?: string
  test_id?: string
  canon_ref?: string
  severity?: string
  origin?: string
}

type SortMode = 'idea' | 'blocking' | 'newest' | 'oldest' | 'priority'
type TabMode = 'todos' | 'propositions'

export default function TodosPage() {
  const [todos, setTodos] = useState<Todo[]>([])
  const [propositions, setPropositions] = useState<Todo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [resolvedId, setResolvedId] = useState<string | null>(null)
  const [ideaFilter, setIdeaFilter] = useState<string>('all')
  const [sortMode, setSortMode] = useState<SortMode>('blocking')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [batchResolving, setBatchResolving] = useState(false)
  const [batchResult, setBatchResult] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabMode>('todos')

  const loadTodos = async () => {
    try {
      setError(null)
      const [todoData, propData] = await Promise.all([
        fetchTodos(undefined, 'todo'),
        fetchTodos(undefined, 'proposition'),
      ])
      setTodos(Array.isArray(todoData) ? todoData : [])
      setPropositions(Array.isArray(propData) ? propData : [])
      setSelectedIds(new Set())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load TODOs')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTodos()
  }, [])

  // Current items based on active tab
  const currentItems = activeTab === 'todos' ? todos : propositions

  // Extract unique Ideas for the filter dropdown
  const ideas = useMemo(() => {
    const set = new Set<string>()
    currentItems.forEach(t => { if (t.idea) set.add(t.idea) })
    return Array.from(set).sort()
  }, [currentItems])

  // Filter and sort
  const displayItems = useMemo(() => {
    let filtered = currentItems
    if (ideaFilter !== 'all') {
      filtered = filtered.filter(t => t.idea === ideaFilter)
    }

    const sorted = [...filtered]
    switch (sortMode) {
      case 'blocking':
        sorted.sort((a, b) => {
          const aBlocking = a.blocking && a.blocking.length > 0 ? 1 : 0
          const bBlocking = b.blocking && b.blocking.length > 0 ? 1 : 0
          if (aBlocking !== bBlocking) return bBlocking - aBlocking
          return (a.idea || '').localeCompare(b.idea || '')
        })
        break
      case 'idea':
        sorted.sort((a, b) => (a.idea || '').localeCompare(b.idea || ''))
        break
      case 'newest':
        break
      case 'priority': {
        const priorityOrder: Record<string, number> = { P0: 0, P1: 1, P2: 2, P3: 3 }
        sorted.sort((a, b) => {
          const aP = priorityOrder[a.priority || ''] ?? 99
          const bP = priorityOrder[b.priority || ''] ?? 99
          if (aP !== bP) return aP - bP
          return (a.idea || '').localeCompare(b.idea || '')
        })
        break
      }
      case 'oldest':
        sorted.reverse()
        break
    }
    return sorted
  }, [currentItems, ideaFilter, sortMode])

  // Per-Idea counts
  const ideaCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    currentItems.forEach(t => {
      const idea = t.idea || 'Unknown'
      counts[idea] = (counts[idea] || 0) + 1
    })
    return counts
  }, [currentItems])

  const handleResolve = async (todoId: string, selectedOption: number | null, notes?: string) => {
    try {
      await resolveTodo(todoId, selectedOption, notes)
      setResolvedId(todoId)
      if (activeTab === 'todos') {
        setTodos(prev => prev.filter(t => t.id !== todoId))
      } else {
        setPropositions(prev => prev.filter(t => t.id !== todoId))
      }
      setSelectedIds(prev => {
        const next = new Set(prev)
        next.delete(todoId)
        return next
      })
      setTimeout(() => setResolvedId(null), 3000)
    } catch (err) {
      throw err
    }
  }

  const handleMarkComplete = async (todoId: string) => {
    try {
      await paradigm.resolveTodo(todoId, null, undefined, true)
      setResolvedId(todoId)
      if (activeTab === 'todos') {
        setTodos(prev => prev.filter(t => t.id !== todoId))
      } else {
        setPropositions(prev => prev.filter(t => t.id !== todoId))
      }
      setSelectedIds(prev => {
        const next = new Set(prev)
        next.delete(todoId)
        return next
      })
      setTimeout(() => setResolvedId(null), 3000)
    } catch (err) {
      throw err
    }
  }

  const toggleSelect = (todoId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(todoId)) {
        next.delete(todoId)
      } else {
        next.add(todoId)
      }
      return next
    })
  }

  const selectAllVisible = () => {
    setSelectedIds(new Set(displayItems.map(t => t.id)))
  }

  const deselectAll = () => {
    setSelectedIds(new Set())
  }

  const handleBatchDismiss = async () => {
    if (selectedIds.size === 0) return
    setBatchResolving(true)
    setBatchResult(null)
    try {
      const result = await paradigm.batchResolveTodos(
        Array.from(selectedIds),
        0,
        'Batch dismissed via TODO page'
      )
      if (activeTab === 'todos') {
        setTodos(prev => prev.filter(t => !selectedIds.has(t.id)))
      } else {
        setPropositions(prev => prev.filter(t => !selectedIds.has(t.id)))
      }
      setSelectedIds(new Set())
      setBatchResult(`Resolved ${result.resolved_count} items${result.not_found_count > 0 ? `, ${result.not_found_count} not found` : ''}${result.error_count > 0 ? `, ${result.error_count} errors` : ''}`)
      setTimeout(() => setBatchResult(null), 5000)
    } catch (err) {
      setBatchResult(`Batch resolve failed: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setBatchResolving(false)
    }
  }

  const switchTab = (tab: TabMode) => {
    setActiveTab(tab)
    setSelectedIds(new Set())
    setIdeaFilter('all')
  }

  if (loading) {
    return (
      <div className="flex-1 overflow-y-auto px-4 py-6 max-w-7xl mx-auto w-full">
        <div className="mb-6">
          <div className="h-8 w-48 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded animate-pulse bg-[length:200%_100%]" />
          <div className="h-4 w-96 mt-2 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded animate-pulse bg-[length:200%_100%]" />
        </div>
        <div className="space-y-6">
          <SkeletonLoader variant="card" count={3} />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-950 border border-red-800 rounded-lg p-4">
        <h2 className="text-red-400 font-medium mb-2">Error Loading TODOs</h2>
        <p className="text-red-300 text-sm">{error}</p>
        <button
          onClick={loadTodos}
          className="mt-3 px-4 py-2 bg-red-900 hover:bg-red-800 text-red-100 rounded text-sm"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 py-6 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Decisions</h1>
          <p className="text-sm text-gray-400 mt-1">
            Review pending decisions and knowledge propositions from the agent system
          </p>
        </div>
        <button
          onClick={loadTodos}
          className="px-3 py-2 text-sm text-gray-400 hover:text-gray-300 border border-gray-700 hover:border-gray-600 rounded transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-900 p-1 rounded-lg border border-gray-800 w-fit">
        <button
          onClick={() => switchTab('todos')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'todos'
              ? 'bg-gray-800 text-white'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          TODOs
          <span className={`px-1.5 py-0.5 rounded text-xs ${
            activeTab === 'todos' ? 'bg-blue-900 text-blue-300' : 'bg-gray-800 text-gray-500'
          }`}>
            {todos.length}
          </span>
        </button>
        <button
          onClick={() => switchTab('propositions')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'propositions'
              ? 'bg-gray-800 text-white'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          Propositions
          <span className={`px-1.5 py-0.5 rounded text-xs ${
            activeTab === 'propositions' ? 'bg-purple-900 text-purple-300' : 'bg-gray-800 text-gray-500'
          }`}>
            {propositions.length}
          </span>
        </button>
      </div>

      {/* Tab Description */}
      {activeTab === 'propositions' && (
        <div className="mb-6 p-3 bg-purple-950/30 border border-purple-900/50 rounded-lg">
          <p className="text-sm text-purple-300">
            Propositions are knowledge claims mined from conversations. Review them to adopt into canon or dismiss.
          </p>
        </div>
      )}

      {/* Success Banner */}
      {resolvedId && (
        <div className="mb-6 bg-green-950 border border-green-800 rounded-lg p-4 flex items-center gap-3">
          <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="text-green-400 font-medium">
              {activeTab === 'todos' ? 'TODO Resolved' : 'Proposition Reviewed'}
            </p>
            <p className="text-green-300 text-sm">Decision recorded: {resolvedId}</p>
          </div>
        </div>
      )}

      {/* Batch Result Banner */}
      {batchResult && (
        <div className={`mb-6 rounded-lg p-4 flex items-center gap-3 ${
          batchResult.includes('failed') ? 'bg-red-950 border border-red-800' : 'bg-green-950 border border-green-800'
        }`}>
          <svg className={`w-5 h-5 flex-shrink-0 ${batchResult.includes('failed') ? 'text-red-400' : 'text-green-400'}`} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <p className={`text-sm font-medium ${batchResult.includes('failed') ? 'text-red-400' : 'text-green-400'}`}>
            {batchResult}
          </p>
        </div>
      )}

      {/* Empty State */}
      {currentItems.length === 0 ? (
        <div className="bg-gray-900 rounded-lg border border-gray-800 p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-300 mb-2">
              {activeTab === 'todos' ? 'No Pending TODOs' : 'No Pending Propositions'}
            </h3>
            <p className="text-gray-500 text-sm">
              {activeTab === 'todos'
                ? 'The agent system has no decisions waiting for your input.'
                : 'No mined knowledge claims are awaiting review.'}
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Controls Bar */}
          <div className="mb-6 flex flex-wrap items-center gap-3 text-sm">
            {/* Total count */}
            <div className="px-3 py-2 bg-gray-900 border border-gray-800 rounded">
              <span className="text-gray-400">Total: </span>
              <span className="font-semibold text-white">{currentItems.length}</span>
              {ideaFilter !== 'all' && (
                <span className="text-gray-500 ml-1">
                  (showing {displayItems.length})
                </span>
              )}
            </div>

            {/* Blocking count (TODOs only) */}
            {activeTab === 'todos' && currentItems.some(t => t.blocking && t.blocking.length > 0) && (
              <div className="px-3 py-2 bg-yellow-950 border border-yellow-800 rounded">
                <span className="text-yellow-400">
                  {currentItems.filter(t => t.blocking && t.blocking.length > 0).length} blocking
                </span>
              </div>
            )}

            {/* Idea filter */}
            <select
              value={ideaFilter}
              onChange={(e) => setIdeaFilter(e.target.value)}
              className="px-3 py-2 bg-gray-900 border border-gray-700 rounded text-gray-300 focus:border-blue-600 focus:outline-none"
            >
              <option value="all">All Ideas</option>
              {ideas.map(idea => (
                <option key={idea} value={idea}>
                  {idea} ({ideaCounts[idea] || 0})
                </option>
              ))}
            </select>

            {/* Sort mode */}
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value as SortMode)}
              className="px-3 py-2 bg-gray-900 border border-gray-700 rounded text-gray-300 focus:border-blue-600 focus:outline-none"
            >
              <option value="blocking">Blocking first</option>
              <option value="idea">Sort by Idea</option>
              <option value="priority">Priority (P0 first)</option>
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
            </select>

            {/* Selection controls */}
            <div className="flex items-center gap-2 ml-auto">
              {selectedIds.size > 0 ? (
                <>
                  <span className="text-gray-400">
                    {selectedIds.size} selected
                  </span>
                  <button
                    onClick={deselectAll}
                    className="px-3 py-2 text-gray-400 hover:text-gray-300 border border-gray-700 hover:border-gray-600 rounded transition-colors"
                  >
                    Deselect
                  </button>
                  <button
                    onClick={handleBatchDismiss}
                    disabled={batchResolving}
                    className="px-3 py-2 bg-orange-700 hover:bg-orange-600 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded transition-colors flex items-center gap-2"
                  >
                    {batchResolving ? (
                      <>
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Resolving...
                      </>
                    ) : (
                      `Dismiss ${selectedIds.size}`
                    )}
                  </button>
                </>
              ) : (
                <button
                  onClick={selectAllVisible}
                  className="px-3 py-2 text-gray-400 hover:text-gray-300 border border-gray-700 hover:border-gray-600 rounded transition-colors"
                >
                  Select all ({displayItems.length})
                </button>
              )}
            </div>
          </div>

          {/* Item Cards */}
          <div className="space-y-6">
            {displayItems.map((todo) => (
              <div key={todo.id} className="flex items-start gap-3">
                {/* Selection checkbox */}
                <button
                  onClick={() => toggleSelect(todo.id)}
                  className={`mt-6 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    selectedIds.has(todo.id)
                      ? 'border-orange-500 bg-orange-600'
                      : 'border-gray-600 hover:border-gray-500'
                  }`}
                >
                  {selectedIds.has(todo.id) && (
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <TodoCard
                    todo={todo}
                    onResolve={handleResolve}
                    onMarkComplete={handleMarkComplete}
                    variant={activeTab === 'propositions' ? 'proposition' : 'todo'}
                  />
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
